import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  InputAdornment,
  Alert,
  CircularProgress,
  IconButton,
  Chip,
} from '@mui/material';
import {
  Search as SearchIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  AccountBalance as BankIcon,
} from '@mui/icons-material';
import { getUsersByRole } from '../../services/userService';
import { assignBanker } from '../../services/assignmentService';
import { ROLE_COLORS } from '../../config/constants';

const AssignBankerDialog = ({ open, onClose, studentId, onAssigned }) => {
  const [bankers, setBankers] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [notes, setNotes] = useState('');
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (open) {
      fetchBankers();
      setSelectedId(null);
      setNotes('');
      setSearch('');
      setError(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const fetchBankers = async () => {
    setLoading(true);
    try {
      const response = await getUsersByRole('banker');
      setBankers(response.data?.data?.users || []);
    } catch (err) {
      setError('Failed to load bankers.');
      console.error('Error fetching bankers:', err);
    } finally {
      setLoading(false);
    }
  };

  // Only show primary bankers (no parentBankerId) — team members can't be assigned to students
  const filteredBankers = bankers
    .filter((b) => !b.parentBankerId)
    .filter((b) =>
      (b.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.email || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.bankName || '').toLowerCase().includes(search.toLowerCase()) ||
      (b.branchName || '').toLowerCase().includes(search.toLowerCase())
    );

  const handleAssign = async () => {
    if (!selectedId) return;
    setSubmitting(true);
    setError(null);
    try {
      await assignBanker(studentId, selectedId, notes);
      if (onAssigned) onAssigned();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to assign banker.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      aria-labelledby="assign-banker-dialog-title"
    >
      <DialogTitle
        id="assign-banker-dialog-title"
        sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
      >
        Assign Banker
        <IconButton size="small" onClick={onClose}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <TextField
          fullWidth
          size="small"
          placeholder="Search by name, email, bank, or branch..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{ mb: 2 }}
          slotProps={{
            input: {
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon fontSize="small" />
                </InputAdornment>
              ),
            },
          }}
        />

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : filteredBankers.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ py: 3 }}>
            No bankers found.
          </Typography>
        ) : (
          <List
            dense
            sx={{
              maxHeight: 350,
              overflow: 'auto',
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
            }}
          >
            {filteredBankers.map((banker) => (
              <ListItem
                key={banker._id}
                onClick={() => setSelectedId(banker._id)}
                selected={selectedId === banker._id}
                sx={{
                  cursor: 'pointer',
                  '&.Mui-selected': {
                    backgroundColor: 'primary.light',
                    '&:hover': { backgroundColor: 'primary.light' },
                  },
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    sx={{
                      bgcolor: ROLE_COLORS.banker,
                      width: 36,
                      height: 36,
                      fontSize: '0.875rem',
                    }}
                  >
                    {banker.name?.charAt(0)?.toUpperCase() || 'B'}
                  </Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={
                    <Typography variant="body2" fontWeight={500}>
                      {banker.name}
                    </Typography>
                  }
                  secondary={
                    <Box>
                      <Typography variant="caption" color="text.secondary" display="block">
                        {banker.email}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5 }}>
                        {banker.bankName && (
                          <Chip
                            icon={<BankIcon sx={{ fontSize: '0.75rem !important' }} />}
                            label={banker.bankName}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                        {banker.branchName && (
                          <Chip
                            label={banker.branchName}
                            size="small"
                            variant="outlined"
                            sx={{ height: 22, fontSize: '0.7rem' }}
                          />
                        )}
                      </Box>
                    </Box>
                  }
                />
                {selectedId === banker._id && (
                  <CheckIcon color="primary" fontSize="small" />
                )}
              </ListItem>
            ))}
          </List>
        )}

        <TextField
          fullWidth
          multiline
          rows={2}
          label="Notes (optional)"
          placeholder="Reason for assignment..."
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleAssign}
          disabled={!selectedId || submitting}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {submitting ? 'Assigning...' : 'Assign Banker'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignBankerDialog;
